import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, FileText, Hourglass, Trophy, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

export default function SchoolParentChildAssessments() {
  const navigate = useNavigate();
  const { cid } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [child, setChild] = useState(null);
  const school = useSchool(child?.school_id);
  const [openTid, setOpenTid] = useState(null);
  const [resultData, setResultData] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/parent/children/${cid}/assessments`);
      setItems(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load assessments');
    } finally { setLoading(false); }
  }, [cid]);

  useEffect(() => {
    (async () => {
      try {
        const cRes = await api.get(`/children/${cid}`);
        setChild(cRes.data);
      } catch {/* */}
    })();
    load();
  }, [cid, load]);

  const brandColor = school?.brand_color || BRAND_COLOR_FALLBACK;

  const toggleResult = async (tid) => {
    if (openTid === tid) {
      setOpenTid(null);
      setResultData(null);
      return;
    }
    setOpenTid(tid);
    setResultLoading(true);
    try {
      const res = await api.get(`/parent/children/${cid}/attempts/${tid}`);
      setResultData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load result');
      setOpenTid(null);
    } finally { setResultLoading(false); }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 pb-12"
      data-testid="school-parent-child-assessments"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/school-parent')} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-1.5" /> Back
          </Button>
          <div>
            <h1 className="font-bold text-slate-900">Assessments</h1>
            <p className="text-xs text-slate-500">{child?.name || 'Your child'}</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-indigo-600" /></div>
        ) : items.length === 0 ? (
          <div className="border-2 border-dashed border-slate-200 rounded-2xl py-16 px-6 text-center bg-white/60" data-testid="empty-parent-assessments">
            <FileText size={26} className="mx-auto text-slate-400 mb-3" />
            <p className="text-slate-900 font-semibold text-lg">No assessments yet</p>
            <p className="text-sm text-slate-600 mt-1">Once your school publishes assessments for {child?.name || 'your child'}, they'll appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((a) => (
              <div key={a.assessment_id} className="bg-white border rounded-xl" data-testid={`parent-asmt-${a.assessment_id}`}>
                <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900 truncate">{a.title}</h3>
                      <StatusPill status={a.student_status} />
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                      {a.due_at && <span>Due {new Date(a.due_at).toLocaleDateString()}</span>}
                      {a.score_text && <span className="text-emerald-700 font-medium">Score: {a.score_text}</span>}
                    </div>
                  </div>
                  {a.student_status === 'released' && a.attempt_id && (
                    <Button variant="outline" size="sm" onClick={() => toggleResult(a.attempt_id)} data-testid={`toggle-result-${a.attempt_id}`}>
                      {openTid === a.attempt_id ? <ChevronUp size={14} className="mr-1" /> : <ChevronDown size={14} className="mr-1" />}
                      {openTid === a.attempt_id ? 'Hide' : 'View result'}
                    </Button>
                  )}
                  {a.student_status === 'submitted' && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                      <Hourglass size={12} className="mr-1" /> Awaiting teacher
                    </Badge>
                  )}
                </div>
                {openTid === a.attempt_id && (
                  <div className="border-t bg-slate-50 p-4">
                    {resultLoading ? (
                      <div className="flex items-center justify-center py-6"><Loader2 className="animate-spin text-indigo-600" /></div>
                    ) : (
                      <ResultDetails data={resultData} brandColor={brandColor} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    not_started: { label: 'Not started', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
    in_progress: { label: 'In progress', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
    submitted: { label: 'Awaiting teacher', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
    released: { label: 'Released', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  };
  const s = map[status] || map.not_started;
  return <Badge variant="outline" className={s.cls}>{s.label}</Badge>;
}

function ResultDetails({ data, brandColor }) {
  if (!data) return null;
  const at = data.attempt;
  const questions = data.questions || [];
  const answersByQid = {};
  for (const ans of at?.answers || []) answersByQid[ans.question_id] = ans;
  const teacherCommentByQid = {};
  for (const item of at?.teacher_feedback_per_question || []) teacherCommentByQid[item.question_id] = item.comment;

  return (
    <div className="space-y-3">
      <div className="bg-white border rounded-xl p-4 text-center" data-testid="parent-result-score">
        <Trophy size={22} className="mx-auto mb-1" style={{ color: brandColor }} />
        <div className="text-2xl font-bold text-slate-900">{at.total_score}/{at.max_score}</div>
        <div className="text-xs text-slate-500">{at.percent}%</div>
      </div>
      {at.teacher_overall_feedback && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-sm" data-testid="parent-overall-feedback">
          <strong className="text-indigo-700">Teacher's feedback:</strong>{' '}
          <span className="text-slate-800 whitespace-pre-wrap">{at.teacher_overall_feedback}</span>
        </div>
      )}
      {questions.map((q, idx) => {
        const ans = answersByQid[q.question_id];
        const correct = q.correct_option_id;
        const chosen = ans?.chosen_option_id;
        const isCorrect = chosen === correct;
        return (
          <div key={q.question_id} className={`bg-white border rounded-xl p-4 ${isCorrect ? 'border-emerald-200' : 'border-rose-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold text-slate-700 text-sm">Q{idx + 1}</span>
              <Badge variant="outline" className={isCorrect ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-rose-50 text-rose-700 border-rose-300'}>
                {isCorrect ? 'Correct' : 'Incorrect'}
              </Badge>
            </div>
            <p className="text-sm font-medium text-slate-800 mb-2">{q.prompt}</p>
            <div className="text-xs text-slate-600 space-y-1">
              <div><strong>Your child chose:</strong> {chosen || '—'}{ans?.student_reason ? ` · "${ans.student_reason}"` : ''}</div>
              <div><strong>Correct answer:</strong> {correct} · {q.options.find(o => o.id === correct)?.text}</div>
              <div className="text-indigo-700"><strong>Reasoning:</strong> {q.correct_reasoning}</div>
              {ans?.reason_score != null && (
                <div
                  className="text-slate-700 mt-1"
                  data-testid={`parent-reason-score-${q.question_id}`}
                >
                  <strong>Reasoning quality:</strong> {ans.reason_score}/5
                  {ans.reason_rationale ? ` — ${ans.reason_rationale}` : ''}
                </div>
              )}
              {teacherCommentByQid[q.question_id] && (
                <div className="text-amber-700"><strong>Teacher's note:</strong> {teacherCommentByQid[q.question_id]}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
